// Component to show image preview with Upload, Download, and Delete buttons

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ImageModal from './ImageModal';
import Spinner from './Spinner';
import { useModal } from './ModalProvider';
import { fileSizeLimit } from '../utils/consts';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { auth, storage } from '../utils/firebase';
import { fileToResizedFile } from '../utils/image';
import { useStorageImageState } from '../utils/storageImage';
import './ImageBtns.scss';

export default function ImageBtns({
  // Public images (teams, tournaments) and the temp/ upload preview come as
  // ready URLs; private user images come as Storage paths and are resolved
  // here, where it is known whether the bytes are actually being looked at.
  imgUrl,
  imgThumbUrl,
  imgPath,
  imgThumbPath,
  imgTitle,
  showTitleOnThumb,
  size,
  clickable,
  downloadable,
  onUploadImg,
  onDeleteImg,
  showUploadWarning,
  placeholder,
}) {
  const [showImgModal, setShowImgModal] = useState(false);
  const [progress, setProgress] = useState(null);
  const [spinner, setSpinner] = useState(false);
  const inputFileRef = useRef();
  const uploadUnsubRef = useRef(null);
  const { openModal } = useModal();

  const thumb = useStorageImageState(imgThumbPath);
  // The original is heavy, so it is fetched only when something shows it: the
  // modal, the download link, or the thumbnail being missing — a brand new
  // upload has none until the backend trigger makes one.
  const full = useStorageImageState(
    imgPath,
    !imgUrl && (showImgModal || !!downloadable || thumb.failed),
  );
  const fullUrl = imgUrl || full.url;

  // The thumbnail is what gets displayed; fall back to the original.
  const shownUrl = imgThumbUrl || thumb.url || fullUrl;
  // Whether an image exists at all — distinct from its bytes having arrived,
  // which a denial or a lagging thumbnail trigger can hold up indefinitely.
  const hasImage = !!(imgUrl || imgThumbUrl || imgPath || imgThumbPath);

  // Unsub on unmount
  useEffect(() => {
    return () => {
      if (uploadUnsubRef.current) {
        uploadUnsubRef.current();
        uploadUnsubRef.current = null;
      }
    };
  }, []);

  const onFileChoose = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file.type || !file.type.startsWith('image/')) {
      toast.error('Wrong file type');
      return;
    }

    if (file.size > fileSizeLimit) {
      toast.error(
        `File size limit is ${(fileSizeLimit / (1024 * 1024)).toFixed(0)} MB`,
      );
      return;
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      toast.error('Sign in to upload an image');
      return;
    }

    try {
      const resizedFile = await fileToResizedFile({ file });
      // Staging is private per user: temp/{uid}/ is the only prefix a client
      // may write, and only its owner can read it back.
      const storageRef = ref(storage, `temp/${uid}/${resizedFile.name}`);
      // Safe to cache forever: fileToResizedFile gives every upload a unique
      // name, so a replacement never reuses this URL. On the way out of temp/
      // the backend restamps it — private for users/, public for the rest —
      // and generateThumbnail stamps the thumbnail the same way.
      const uploadTask = uploadBytesResumable(storageRef, resizedFile, {
        cacheControl: 'public, max-age=31536000, immutable',
        contentType: resizedFile.type,
      });
      setProgress(0);

      const unsub = uploadTask.on(
        'state_changed',
        (state) =>
          setProgress((state.bytesTransferred / state.totalBytes) * 100),
        (error) => {
          toast.error(error?.message ?? 'Upload failed');
          setProgress(null);
        },
        async () => {
          try {
            await onUploadImg(resizedFile);
            setProgress(null);
            setSpinner(true);
          } catch (error) {
            toast.error(error?.message ?? 'Uploading went wrong');
            setProgress(null);
          } finally {
            if (uploadUnsubRef.current) {
              uploadUnsubRef.current();
              uploadUnsubRef.current = null;
            }
          }
        },
      );

      uploadUnsubRef.current = unsub;
    } catch (error) {
      toast.error(error?.message ?? 'Uploading went wrong');
    }
  };

  useEffect(() => {
    setSpinner(false);
  }, [shownUrl]);

  // Nothing to open: the modal would be a black rectangle over a spinner that
  // never stops, so say why and stay on the card.
  useEffect(() => {
    if (!showImgModal || !full.failed) return;

    setShowImgModal(false);
    toast.error('Failed to load the image');
  }, [showImgModal, full.failed]);

  const triggerUpload = () => {
    if (showUploadWarning) {
      openModal({
        header: 'Photo Requirements',
        body: (
          <div>
            <p>Photo must be current. Headshot with clear image.</p>
          </div>
        ),
        buttons: [{ text: 'OK', onClick: () => inputFileRef.current.click() }],
      });
    } else {
      inputFileRef.current.click();
    }
  };

  return (
    <div className='image-btns' style={{ width: size + 60 + 'px' }}>
      {shownUrl && progress === null && (
        <div
          className={'bg-image' + (clickable ? ' clickable' : '')}
          style={{
            backgroundImage: `url(${shownUrl})`,
            width: size,
            height: size,
          }}
          onClick={
            hasImage && clickable ? () => setShowImgModal(true) : undefined
          }
        />
      )}
      {!shownUrl && placeholder && progress === null && (
        <div
          className='bg-image'
          style={{
            backgroundImage: `url(${placeholder})`,
            width: size,
            height: size,
          }}
        />
      )}
      {!shownUrl && !placeholder && progress === null && (
        <div
          className={'upload-icon-wrap' + (onUploadImg ? ' upload' : '')}
          style={{
            width: size,
            height: size,
          }}
          onClick={onUploadImg ? triggerUpload : undefined}
        >
          <p className='fa-upload gray-600' />
        </div>
      )}
      {progress !== null && (
        <div
          className='progress-wrap'
          style={{
            width: size,
            height: size,
          }}
        >
          <div className='progress-line'>
            <div className='progress' style={{ width: progress + '%' }} />
          </div>
          <p className='font-medium'>Uploading {Math.trunc(progress)}%</p>
        </div>
      )}
      <Spinner show={spinner || (showImgModal && !fullUrl)} />
      <label
        className={
          'image-title' + (imgTitle && showTitleOnThumb ? '' : ' no-title')
        }
      >
        {imgTitle || 'No title'}
      </label>
      <div className='btns'>
        {downloadable && fullUrl && (
          <Link
            to={fullUrl}
            target='_blank'
            onClick={(e) => e.stopPropagation()}
            className='button bw small'
          >
            Download
          </Link>
        )}
        {onUploadImg && (
          <button type='button' className='small bw' onClick={triggerUpload}>
            Upload
          </button>
        )}
        {onDeleteImg && hasImage && (
          <button
            type='button'
            className='small danger'
            onClick={() =>
              openModal({
                header: 'Delete image',
                body: (
                  <p className='font-16'>
                    Are you sure you want to delete the image?
                  </p>
                ),
                buttons: [
                  {
                    text: 'Cancel',
                    className: 'bw',
                  },
                  {
                    text: 'Delete',
                    className: 'danger',
                    onClick: async () => {
                      try {
                        setSpinner(true);
                        await onDeleteImg();
                      } catch (error) {
                        setSpinner(false);
                        toast.error(error?.message ?? 'Deleting went wrong');
                      }
                    },
                  },
                ],
              })
            }
          >
            Delete
          </button>
        )}
      </div>
      <input
        type='file'
        accept='image/*'
        onChange={(e) => onFileChoose(e)}
        ref={inputFileRef}
        className='file-upload-input'
      />
      <ImageModal
        show={showImgModal && !!fullUrl}
        imgUrl={fullUrl}
        title={imgTitle}
        onClose={() => setShowImgModal(false)}
        downloadable={downloadable}
        clickable
      />
    </div>
  );
}
